'use client';

import { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import styles from './suffaDars.module.css';
import hadiyaLogo from '../assets/hadiya-logo-web.png';
import icfLogo from '../assets/icf_logo.png';

const GCC_NATIONS = [
  'UAE',
  'Saudi Arabia',
  'Qatar',
  'Oman',
  'Bahrain',
  'Kuwait',
  'India',
];

const NATION_TO_CODE = {
  'UAE': 'AE',
  'Saudi Arabia': 'SA',
  'Qatar': 'QA',
  'Oman': 'OM',
  'Bahrain': 'BH',
  'Kuwait': 'KW',
  'India': 'IN',
};

const COUNTRY_CODES = [
  { code: '+91', label: '🇮🇳 +91', country: 'India' },
  { code: '+971', label: '🇦🇪 +971', country: 'UAE' },
  { code: '+966', label: '🇸🇦 +966', country: 'Saudi' },
  { code: '+974', label: '🇶🇦 +974', country: 'Qatar' },
  { code: '+968', label: '🇴🇲 +968', country: 'Oman' },
  { code: '+973', label: '🇧🇭 +973', country: 'Bahrain' },
  { code: '+965', label: '🇰🇼 +965', country: 'Kuwait' },
];

const BASE_PATH = '/suffa_dars';

export default function SuffaDarsPage() {
  // Form state
  const [isHadiyaStudent, setIsHadiyaStudent] = useState(null);
  const [rollNo, setRollNo] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);

  // Non-Hadiya fields
  const [name, setName] = useState('');
  const [nation, setNation] = useState('');
  const [place, setPlace] = useState('');

  // Common fields
  const [mobileCountryCode, setMobileCountryCode] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [whatsappCountryCode, setWhatsappCountryCode] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [isWhatsappSame, setIsWhatsappSame] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  // Classrooms data
  const [classroomsData, setClassroomsData] = useState([]);

  useEffect(() => {
    // Fetch classrooms on mount
    fetch(`${BASE_PATH}/api/classrooms`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setClassroomsData(data.classrooms || []);
        }
      })
      .catch(err => console.error('Failed to fetch classrooms:', err));
  }, []);

  // ---- Handlers ----

  const handleStudentToggle = (value) => {
    setIsHadiyaStudent(value);
    setRollNo('');
    setLookupResult(null);
    setLookupError('');
    setName('');
    setNation('');
    setPlace('');
    setError('');
  };

  const handleLookup = async () => {
    if (!rollNo.trim()) {
      setLookupError('Please enter a roll number');
      return;
    }

    setIsLookingUp(true);
    setLookupError('');
    setLookupResult(null);

    try {
      const res = await fetch(`${BASE_PATH}/api/lookup?rollNo=${encodeURIComponent(rollNo.trim())}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Lookup failed');
      }

      if (data.found) {
        setLookupResult(data);
        
        // Helper to parse country code and number
        const parsePhone = (phoneStr, setCode, setNum) => {
          if (!phoneStr) return;
          const str = phoneStr.toString().trim();
          const matchedCode = COUNTRY_CODES.find(c => str.startsWith(c.code));
          if (matchedCode) {
            setCode(matchedCode.code);
            setNum(str.substring(matchedCode.code.length).trim());
          } else {
            setNum(str);
          }
        };

        if (data.mobile) parsePhone(data.mobile, setMobileCountryCode, setMobileNumber);
        if (data.whatsapp) parsePhone(data.whatsapp, setWhatsappCountryCode, setWhatsappNumber);
        
      } else {
        setLookupError(data.message || 'Student not found. Please check the roll number and try again.');
      }
    } catch (err) {
      setLookupError('Failed to look up student. Please try again.');
      console.error('Lookup error:', err);
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const finalName = isHadiyaStudent ? lookupResult?.name : name;
    const finalNation = isHadiyaStudent ? lookupResult?.nation : nation;
    const finalClassroom = isHadiyaStudent ? lookupResult?.classroom : null;
    const finalPlace = isHadiyaStudent ? null : place;

    // Validation
    if (isHadiyaStudent === null) {
      setError('Please select whether you are a Hadiya student');
      return;
    }

    if (isHadiyaStudent && !lookupResult) {
      setError('Please look up your roll number first');
      return;
    }

    if (!isHadiyaStudent && !name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!isHadiyaStudent && !nation) {
      setError('Please select your nation');
      return;
    }

    if (!isHadiyaStudent && !place.trim()) {
      setError('Please enter your place');
      return;
    }

    if (!mobileCountryCode) {
      setError('Please select a mobile country code');
      return;
    }

    if (!mobileNumber.trim()) {
      setError('Please enter your mobile number');
      return;
    }

    if (!whatsappCountryCode) {
      setError('Please select a WhatsApp country code');
      return;
    }

    if (!whatsappNumber.trim()) {
      setError('Please enter your WhatsApp number');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        isHadiyaStudent,
        hadiyaRollNo: isHadiyaStudent ? rollNo : null,
        hadiyaStudentId: isHadiyaStudent ? lookupResult?.studentId : null,
        isTeenHadiya: lookupResult?.isTeenHadiya || false,
        name: finalName,
        classroom: finalClassroom,
        nation: finalNation,
        place: finalPlace,
        mobileCountryCode,
        mobileNumber: mobileNumber.trim(),
        whatsappCountryCode,
        whatsappNumber: whatsappNumber.trim(),
      };

      const res = await fetch(`${BASE_PATH}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      setSubmittedData({
        name: finalName,
        nation: finalNation,
        classroom: finalClassroom,
        place: finalPlace,
        mobile: `${mobileCountryCode} ${mobileNumber}`,
        whatsapp: `${whatsappCountryCode} ${whatsappNumber}`,
        isHadiyaStudent,
        rollNo: data.suffaRollNo,
      });
      setIsSuccess(true);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      console.error('Submit error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterAnother = () => {
    setIsSuccess(false);
    setSubmittedData(null);
    setIsHadiyaStudent(null);
    setRollNo('');
    setLookupResult(null);
    setLookupError('');
    setName('');
    setNation('');
    setPlace('');
    setMobileCountryCode('');
    setMobileNumber('');
    setWhatsappCountryCode('');
    setWhatsappNumber('');
    setError('');
  };

  // ---- SUCCESS SCREEN ----
  if (isSuccess && submittedData) {
    return (
      <div className={styles.darsBody}>
        <div className={styles.card}>
          <div className={styles.header}>
            <img
              src={hadiyaLogo}
              alt="Hadiya Logo"
              className={styles.headerLogo}
            />
            <h1 className={styles.headerTitle}>Suffa Dars</h1>
            <p className={styles.headerSub}>Student Registration</p>
          </div>

          <div className={styles.successContainer}>
            <div className={styles.successCheckWrapper}>
              <CheckCircle className={styles.successCheck} />
            </div>

            <h2 className={styles.successTitle}>Registration Successful!</h2>
            <p className={styles.successSubtitle}>Your details have been submitted successfully</p>

            <div className={styles.summaryCard}>
              <div className={styles.summaryTitle}>Registration Summary</div>

              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Name</span>
                <span className={styles.summaryValue}>{submittedData.name}</span>
              </div>

              {submittedData.rollNo && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Roll No</span>
                  <span className={styles.summaryValue}>{submittedData.rollNo}</span>
                </div>
              )}

              {submittedData.classroom && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Classroom</span>
                  <span className={styles.summaryValue}>{submittedData.classroom}</span>
                </div>
              )}

              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Nation</span>
                <span className={styles.summaryValue}>{submittedData.nation}</span>
              </div>

              {submittedData.place && (
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Place</span>
                  <span className={styles.summaryValue}>{submittedData.place}</span>
                </div>
              )}

              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Mobile</span>
                <span className={styles.summaryValue}>{submittedData.mobile}</span>
              </div>

              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>WhatsApp</span>
                <span className={styles.summaryValue}>{submittedData.whatsapp}</span>
              </div>
            </div>

            <button className={styles.registerAnotherBtn} onClick={handleRegisterAnother}>
              Register Another Student
            </button>
          </div>
        </div>

        {/* ICF Footer */}
        <div className={styles.footer}>
          <img src={icfLogo} alt="ICF International Logo" className={styles.footerLogo} />
        </div>
      </div>
    );
  }

  // ---- MAIN FORM ----
  return (
    <div className={styles.darsBody}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <img
            src={hadiyaLogo}
            alt="Hadiya Logo"
            className={styles.headerLogo}
          />
          <h1 className={styles.headerTitle}>Suffa Dars</h1>
          <p className={styles.headerSub}>Student Registration</p>
        </div>

        {/* Form */}
        <form className={styles.formBody} onSubmit={handleSubmit}>
          {error && <div className={styles.errorBanner}>{error}</div>}

          {/* Step 1: Hadiya Student Toggle */}
          <div className={styles.toggleWrapper}>
            <label className={styles.toggleLabel}>
              Are you a Hadiya student? <span className={styles.required}>*</span>
            </label>
            <div className={styles.toggleGroup}>
              <button
                type="button"
                className={`${styles.toggleBtn} ${isHadiyaStudent === true ? styles.toggleBtnActive : ''}`}
                onClick={() => handleStudentToggle(true)}
              >
                Yes
              </button>
              <button
                type="button"
                className={`${styles.toggleBtn} ${isHadiyaStudent === false ? styles.toggleBtnActive : ''}`}
                onClick={() => handleStudentToggle(false)}
              >
                No
              </button>
            </div>
          </div>

          {/* Hadiya Student Flow: Roll Number Lookup */}
          {isHadiyaStudent === true && (
            <div className={styles.lookupGroup}>
              <label className={styles.formLabel}>
                Hadiya Roll Number <span className={styles.required}>*</span>
              </label>
              <div className={styles.lookupRow}>
                <input
                  type="text"
                  className={styles.lookupInput}
                  placeholder="Enter your roll number"
                  value={rollNo}
                  onChange={(e) => {
                    setRollNo(e.target.value);
                    setLookupResult(null);
                    setLookupError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleLookup();
                    }
                  }}
                />
                <button
                  type="button"
                  className={styles.lookupBtn}
                  onClick={handleLookup}
                  disabled={isLookingUp || !rollNo.trim()}
                >
                  {isLookingUp ? <span className={styles.spinner}></span> : 'Look Up'}
                </button>
              </div>

              {lookupError && <div className={styles.lookupError}>{lookupError}</div>}

              {lookupResult && lookupResult.found && (
                <div className={styles.studentInfoCard}>
                  <div className={styles.studentInfoTitle}>Student Found</div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Name</span>
                    <span className={styles.infoValue}>{lookupResult.name}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Classroom</span>
                    <span className={styles.infoValue}>{lookupResult.classroom}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Nation</span>
                    <span className={styles.infoValue}>{lookupResult.nation}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Non-Hadiya Student Flow: Name, Nation, Place */}
          {isHadiyaStudent === false && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="sd-name">
                  Full Name <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="sd-name"
                  className={styles.formInput}
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="sd-nation">
                  Nation <span className={styles.required}>*</span>
                </label>
                <select
                  id="sd-nation"
                  className={styles.formSelect}
                  value={nation}
                  onChange={(e) => {
                    setNation(e.target.value);
                    setPlace('');
                  }}
                >
                  <option value="">-- Select Nation --</option>
                  {GCC_NATIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="sd-place">
                  Place <span className={styles.required}>*</span>
                </label>
                {nation === 'India' ? (
                  <input
                    type="text"
                    id="sd-place"
                    className={styles.formInput}
                    value={place}
                    onChange={(e) => setPlace(e.target.value)}
                    placeholder="Enter your place"
                  />
                ) : (
                  <select
                    id="sd-place"
                    className={styles.formSelect}
                    value={place}
                    onChange={(e) => setPlace(e.target.value)}
                    disabled={!nation}
                  >
                    <option value="">-- Select Place --</option>
                    {classroomsData
                      .filter(c => c.country_code === NATION_TO_CODE[nation])
                      .map((c) => (
                        <option key={c.id} value={c.description}>{c.description}</option>
                    ))}
                  </select>
                )}
              </div>
            </>
          )}

          {/* Contact Information */}
          {isHadiyaStudent !== null && (isHadiyaStudent === false || lookupResult) && (
            <>
              <div className={styles.sectionDivider}>
                <span className={styles.sectionLabel}>Contact Information</span>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Mobile Number <span className={styles.required}>*</span>
                </label>
                <div className={styles.phoneRow}>
                  <select
                    className={styles.countryCodeSelect}
                    value={mobileCountryCode}
                    onChange={(e) => {
                      setMobileCountryCode(e.target.value);
                      if (isWhatsappSame) setWhatsappCountryCode(e.target.value);
                    }}
                  >
                    <option value="">-- Select Code --</option>
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    className={styles.phoneInput}
                    placeholder="Enter mobile number"
                    value={mobileNumber}
                    onChange={(e) => {
                      setMobileNumber(e.target.value);
                      if (isWhatsappSame) setWhatsappNumber(e.target.value);
                    }}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  WhatsApp Number <span className={styles.required}>*</span>
                </label>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <input
                    type="checkbox"
                    id="same-as-mobile"
                    checked={isWhatsappSame}
                    onChange={(e) => {
                      setIsWhatsappSame(e.target.checked);
                      if (e.target.checked) {
                        setWhatsappCountryCode(mobileCountryCode);
                        setWhatsappNumber(mobileNumber);
                      }
                    }}
                    style={{ width: '16px', height: '16px', accentColor: '#4f46e5' }}
                  />
                  <label htmlFor="same-as-mobile" style={{ fontSize: '0.9rem', color: '#475569', cursor: 'pointer' }}>
                    Same as Mobile Number
                  </label>
                </div>

                <div className={styles.phoneRow}>
                    <select
                      className={styles.countryCodeSelect}
                      value={whatsappCountryCode}
                      onChange={(e) => setWhatsappCountryCode(e.target.value)}
                      disabled={isWhatsappSame}
                    >
                      <option value="">-- Select Code --</option>
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                  <input
                    type="tel"
                    className={styles.phoneInput}
                    placeholder="Enter WhatsApp number"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    disabled={isWhatsappSame}
                  />
                </div>
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={isSubmitting}
              >
                {isSubmitting ? <span className={styles.spinner}></span> : 'Submit Registration'}
              </button>
            </>
          )}
        </form>
      </div>

      {/* ICF Footer */}
      <div className={styles.footer}>
        <img src={icfLogo} alt="ICF International Logo" className={styles.footerLogo} />
      </div>
    </div>
  );
}
